import React, { useState, useEffect } from 'react';
import {
  Box, TextField, Button, Typography, CircularProgress, Alert, Snackbar, Paper,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import apiService from '../services/apiService';

const MAX_LEN = 2000;

interface OrganizationSettings {
  org_id: string;
  name: string;
  companyContext: string;
}

/**
 * Company Context — admin-editable description of the organization: what
 * industry it operates in, the products/services it offers, who its potential
 * customers are, and what makes it different. Used as grounding context by
 * various AI-driven features across the platform (training simulations,
 * post-call analysis, etc.).
 *
 * Rendered as the first tab on the Knowledge Base page.
 */
const CompanyContextCard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [context, setContext] = useState('');
  const [original, setOriginal] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [savedToast, setSavedToast] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const resp = await apiService.get<OrganizationSettings>('/settings/organization');
        if (cancelled) return;
        const ctx = resp?.data?.companyContext ?? '';
        setContext(ctx);
        setOriginal(ctx);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load company context');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const dirty = context !== original;
  const remaining = MAX_LEN - context.length;
  const overLimit = remaining < 0;

  const handleSave = async () => {
    if (!dirty || overLimit) return;
    try {
      setSaving(true);
      setError(null);
      const resp = await apiService.put<OrganizationSettings>(
        '/settings/organization',
        { companyContext: context }
      );
      const saved = resp?.data?.companyContext ?? context;
      setOriginal(saved);
      setContext(saved);
      setSavedToast(true);
    } catch (e: any) {
      setError(e?.message || 'Failed to save company context');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider' }}>
      <Box mb={2}>
        <Typography variant="body2" color="text.secondary">
          A concise overview of your organization — the industry you operate in,
          the products or services you offer, your potential customers, and what
          differentiates you in the market. This context is referenced by
          various AI features across the platform to ground their understanding
          of your business.
        </Typography>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          <TextField
            fullWidth
            multiline
            minRows={8}
            maxRows={18}
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder={
              "Example: We make sodium-hydroxide-based industrial cleaning " +
              "systems for airport ground-handling equipment. Our products " +
              "displace legacy degreaser chemistries that are slower, more " +
              "hazardous, and harder to dispose of. Potential customers include " +
              "operations directors at airlines and ground handlers with 50+ " +
              "aircraft in daily operation. Key differentiators: 3× faster " +
              "cleaning cycle, fully biodegradable, regulatory-compliant in 40+ " +
              "jurisdictions."
            }
            variant="outlined"
            error={overLimit}
            inputProps={{ style: { fontSize: 14 } }}
          />
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mt={2}
          >
            <Typography
              variant="caption"
              color={overLimit ? 'error' : 'text.secondary'}
            >
              {overLimit
                ? `Over by ${-remaining} characters — please trim`
                : `${remaining} characters remaining (max ${MAX_LEN})`}
            </Typography>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              disabled={!dirty || overLimit || saving}
              onClick={handleSave}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Box>
        </>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <Snackbar
        open={savedToast}
        autoHideDuration={3000}
        onClose={() => setSavedToast(false)}
        message="Company context saved"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Paper>
  );
};

export default CompanyContextCard;
